"use client";
/**
 * React 래퍼 (drop-in) — 구 @lumir-company/editor 의 <LumirEditor /> 컴포넌트 호환.
 *
 *   // 기존:  import LumirEditor, { ContentUtils } from "@lumir-company/editor";
 *   // 변경:  import LumirEditor, { ContentUtils } from "@lumir-company/editor/react";
 *
 * 내부적으로 react-free 코어(mountLumirEditor)를 useEffect 에서 마운트한다.
 * react 는 이 서브패스에서만 import 되므로 루트(".")는 여전히 react 의존이 없다.
 *
 * - props 는 구 컴포넌트와 동일한 이름을 그대로 전달(initialContent/editable/theme/
 *   tables/heading/onContentChange/onError/onSelectionChange/onImageDelete/ … ).
 * - editable/theme 는 리렌더 시 반응적으로 반영(setEditable/setTheme).
 * - 콜백류는 ref 로 최신값을 추적해 remount 없이 항상 최신 핸들러 호출.
 * - ref 로 { getDocument, undo, redo, canUndo, canRedo, insertFile, editor } 노출(보너스).
 */
import * as React from "react";
import { mountLumirEditor } from "./editor.js";

// 루트의 모든 유틸(ContentUtils/EditorConfig/colors/fontSize/errors/api/…)을 한 경로에서 쓰도록 재노출
export * from "./index.js";

export const LumirEditor = React.forwardRef(function LumirEditor(props, ref) {
  const { className = "", style, ...rest } = props || {};

  const hostRef = React.useRef(null);
  const edRef = React.useRef(null);

  // 콜백/업로더는 ref 로 최신값 유지 → remount 없이 최신 핸들러 사용
  const cbRef = React.useRef({});
  cbRef.current = {
    onContentChange: rest.onContentChange,
    onError: rest.onError,
    onSelectionChange: rest.onSelectionChange,
    onImageDelete: rest.onImageDelete,
    uploadFile: rest.uploadFile,
  };

  // 최초 마운트 옵션 스냅샷(초기 콘텐츠/구성 토글). 이후 변경은 반응 effect로 처리.
  const mountOptsRef = React.useRef(null);
  if (mountOptsRef.current === null) mountOptsRef.current = { ...rest };

  React.useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const opts = { ...mountOptsRef.current };
    // 안정적 래퍼 → 항상 최신 콜백 호출
    opts.onContentChange = (...a) => cbRef.current.onContentChange && cbRef.current.onContentChange(...a);
    opts.onError = (...a) => cbRef.current.onError && cbRef.current.onError(...a);
    opts.onSelectionChange = (...a) => cbRef.current.onSelectionChange && cbRef.current.onSelectionChange(...a);
    opts.onImageDelete = (...a) => cbRef.current.onImageDelete && cbRef.current.onImageDelete(...a);
    if (mountOptsRef.current.uploadFile) opts.uploadFile = (...a) => cbRef.current.uploadFile && cbRef.current.uploadFile(...a);

    const ed = mountLumirEditor(host, opts);
    edRef.current = ed;
    return () => {
      try { ed && ed.destroy && ed.destroy(); } catch (_) {}
      edRef.current = null;
    };
    // 최초 1회만 마운트(BlockNote 원본도 initialContent 변경으로 재설정하지 않음)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // editable 반응
  React.useEffect(() => {
    const ed = edRef.current;
    if (ed && ed.setEditable) ed.setEditable(rest.editable !== false);
  }, [rest.editable]);

  // theme 반응
  React.useEffect(() => {
    const ed = edRef.current;
    if (ed && ed.setTheme) ed.setTheme(rest.theme || "light");
  }, [rest.theme]);

  React.useImperativeHandle(
    ref,
    () => ({
      getDocument: () => (edRef.current ? edRef.current.getDocument() : null),
      undo: () => (edRef.current ? edRef.current.undo() : false),
      redo: () => (edRef.current ? edRef.current.redo() : false),
      canUndo: () => (edRef.current ? edRef.current.canUndo() : false),
      canRedo: () => (edRef.current ? edRef.current.canRedo() : false),
      insertFile: (file) => (edRef.current ? edRef.current.insertFile(file) : undefined),
      setTheme: (t) => edRef.current && edRef.current.setTheme(t),
      setEditable: (v) => edRef.current && edRef.current.setEditable(v),
      get editor() {
        return edRef.current;
      },
    }),
    []
  );

  // "bn-container"는 BlockNote 래퍼 클래스 별칭(소비처 CSS 호환).
  return React.createElement("div", { ref: hostRef, className: cnJoin("lumir-react-host bn-container", className), style });
});

function cnJoin(a, b) {
  return [a, b].filter(Boolean).join(" ");
}

/**
 * 호환 스텁 — 구 @lumir-company/editor 는 독립 <FloatingMenu editor=… /> 컴포넌트를 export 했다.
 * vanilla 는 상단 플로팅 메뉴를 `<LumirEditor floatingMenu />` prop 으로 제공하므로 standalone 은
 * 불필요. 그러나 `import { FloatingMenu }` 가 깨지지 않도록 null 렌더 스텁 + 1회 안내를 둔다.
 */
let _fmWarned = false;
export function FloatingMenu() {
  if (!_fmWarned && typeof console !== "undefined") {
    _fmWarned = true;
    console.warn(
      "[@lumir-company/editor] FloatingMenu 독립 컴포넌트는 더 이상 사용하지 않습니다. " +
        "<LumirEditor floatingMenu floatingMenuPosition=\"sticky|fixed\" /> prop 으로 대체하세요."
    );
  }
  return null;
}

/** 호환 스텁 — 구 패키지의 독립 FontSizeButton(포매팅 툴바용). vanilla 는 툴바에 내장되어 불필요. */
let _fsbWarned = false;
export function FontSizeButton() {
  if (!_fsbWarned && typeof console !== "undefined") {
    _fsbWarned = true;
    console.warn(
      "[@lumir-company/editor] FontSizeButton 독립 컴포넌트는 더 이상 사용하지 않습니다. " +
        "글자크기 컨트롤은 포매팅 툴바에 내장되어 있습니다."
    );
  }
  return null;
}

export default LumirEditor;
